import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Slide, Slides } from "pages/media/types/";
import { useTemplateData } from "./useTemplateData";

// One queue per Slides asset, shared by every caller, so saves never overlap.
const saveQueues = new Map<string, Promise<unknown>>();

export const useSaveSlidesAsset = ({
  organizationId,
  projectId,
}: {
  organizationId?: string;
  projectId?: string;
}) => {
  const queryClient = useQueryClient();
  const {
    templatesData: { slidesPath },
  } = useTemplateData();

  const { saveProjectAsset } = useMediaAssetsStorage({
    organizationId,
    projectId,
    enabled: Boolean(projectId),
  });

  const saveSlidesAsset = useCallback(
    async (assetId: string, slides: Slide[]) => {
      if (!assetId) {
        console.warn("Cannot save slides asset: assetId is missing");
        return;
      }

      const previous = saveQueues.get(assetId) ?? Promise.resolve();
      const run = previous
        .catch(() => undefined)
        .then(() => {
          // Send the latest cached list, not the caller's possibly stale copy.
          const latestSlides =
            queryClient.getQueryData<Slides>(["slides", slidesPath])?.slides ??
            slides;
          return saveProjectAsset("Slides", { slides: latestSlides }, assetId);
        });
      saveQueues.set(assetId, run);

      try {
        await run;
      } catch (error) {
        console.error("Error saving slides asset", error);
        throw error;
      } finally {
        if (saveQueues.get(assetId) === run) saveQueues.delete(assetId);
      }
    },
    [queryClient, slidesPath, saveProjectAsset]
  );

  return {
    saveSlidesAsset,
  };
};
